/**
 * Frame-synchronized scheduling for visual updates.
 * Ported from Worldmonitor (MIT) — see THIRD-PARTY-NOTICES.md.
 */

/**
 * Wrap a function so repeated calls within one frame collapse into a single
 * invocation on the next animation frame, always with the latest arguments.
 * `cancel()` drops a pending invocation (used on teardown).
 */
export function rafSchedule<T extends (...args: unknown[]) => void>(
  fn: T,
): ((...args: Parameters<T>) => void) & { cancel(): void } {
  let scheduled = false;
  let rafId = 0;
  let lastArgs: Parameters<T> | null = null;

  const wrapped = (...args: Parameters<T>): void => {
    lastArgs = args;
    if (!scheduled) {
      scheduled = true;
      rafId = requestAnimationFrame(() => {
        scheduled = false;
        if (lastArgs) {
          fn(...lastArgs);
          lastArgs = null;
        }
      });
    }
  };

  wrapped.cancel = (): void => {
    cancelAnimationFrame(rafId);
    scheduled = false;
    lastArgs = null;
  };

  return wrapped;
}
